/**
 * Cancel authorization — client signing primitives (Batch B).
 *
 * Produces the wallet signature the backend (/dex/cancel → verifyCancelAuthorization) requires to prove
 * the caller owns the order. The canonical payload binds transferId + wallet + nonce + deadline (+
 * chainId in the EVM EIP-712 domain) so a captured signature is not a replayable bearer token. The user
 * sees the human-readable fields they are signing (anti-phishing).
 *
 * Mirrors apps/api/src/services/dex/cancel-auth.ts byte-for-byte (domain/types + buildCancelMessage).
 */
import { ethers } from 'ethers';
import bs58 from 'bs58';
// CANCEL_DOMAIN, CANCEL_TYPES, and buildCancelMessage are imported from @parse/shared — the SAME module
// the API verifier imports — so the signed content can never drift between signer and verifier.
import { CANCEL_DOMAIN, CANCEL_TYPES, buildCancelMessage, type CancelMessageFields } from '@parse/shared';

export { CANCEL_DOMAIN, CANCEL_TYPES, buildCancelMessage };

/** Fields that go into a cancel authorization (alias of the shared type for local readability). */
export type CancelFields = CancelMessageFields;

/** Wallet-signed payload POSTed to /dex/cancel. */
export interface CancelAuthPayload {
  transferId: string;
  signature: string;
  wallet: string;
  nonce: string;
  deadline: number;
}

/** Single-use client nonce. */
export function generateCancelNonce(): string {
  return crypto.randomUUID();
}

/** Short deadline — now + 5 minutes (unix seconds). */
export function cancelDeadline(nowMs: number = Date.now()): number {
  return Math.floor(nowMs / 1000) + 300;
}

/** The human-readable fields to DISPLAY at the cancel confirmation (anti-phishing). */
export function cancelDisplayFields(i: CancelFields): Array<{ label: string; value: string }> {
  return [
    { label: 'Action', value: 'Cancel cross-chain order' },
    { label: 'Order', value: i.transferId },
    { label: 'Chain', value: i.chain },
    { label: 'Expires', value: new Date(i.deadline * 1000).toLocaleString() },
  ];
}

/**
 * EVM: sign the Cancel EIP-712 typed data (chainId-bound). `signer` must be on the source chain;
 * `chainId` is the source chain's id (the value the backend verifies against).
 */
export async function signCancelEvm(signer: ethers.Signer, chainId: number, f: CancelFields): Promise<string> {
  return signer.signTypedData(
    { ...CANCEL_DOMAIN, chainId },
    CANCEL_TYPES as unknown as Record<string, Array<{ name: string; type: string }>>,
    { transferId: f.transferId, wallet: f.wallet, nonce: f.nonce, deadline: BigInt(f.deadline) },
  );
}

/**
 * Tron: sign the canonical message via the wallet adapter's signMessageV2 (returns the signature string).
 * `tronSignMessageV2` is the adapter's bound signer (e.g. tronWeb.trx.signMessageV2 / TronLink adapter).
 */
export async function signCancelTron(
  tronSignMessageV2: (message: string) => Promise<string>,
  f: CancelFields,
): Promise<string> {
  return tronSignMessageV2(buildCancelMessage(f));
}

/**
 * Solana: sign the canonical message bytes via the wallet adapter's signMessage, then base58-encode the
 * 64-byte signature. The backend ed25519-verifies it with bs58.decode(signature) + a 64-byte length check
 * (apps/api/.../cancel-auth.ts verifySolana), so the signature MUST be base58 — hex/base64 will not verify.
 * We encode with bs58 (the same library the backend decodes with) so the encoding is symmetric by design.
 */
export async function signCancelSolana(
  signMessage: (message: Uint8Array) => Promise<Uint8Array>,
  f: CancelFields,
): Promise<string> {
  const sig = await signMessage(new TextEncoder().encode(buildCancelMessage(f)));
  return bs58.encode(sig);
}
